/**
 * The fine print every governed object carries (owner ruling 2026-10-15): the
 * org chart and product tree versions it was made under.
 *
 *   made     frozen things — a session result, a priority list, a report:
 *            "Made under org chart v1.2 · product tree v1.0"
 *   locked   a locked pool: "Locked under org chart v1.2"
 *   started  live things — an item, cycle, track, session, plan:
 *            "Started under org chart v1.2 (now v1.4)"
 *
 * A tree the workspace has never versioned is left out; a row made before
 * versions were kept says so instead of guessing. Admins can follow a version
 * to its history.
 */

import { Link } from 'react-router-dom';
import { formatRevisionVersion, structureHistoryAnchorId, type StructureRevision } from '@/lib/hierarchy/structureRevisions';
import { useStructurePins, type PinnedTable } from '@/hooks/hierarchy/useStructurePins';
import { useUserStore } from '@/stores/prioritization/userStore';

const LEAD = { made: 'Made under', locked: 'Locked under', started: 'Started under' } as const;
const PAGE = { org: '/prioritization/settings/org-hierarchy', product: '/prioritization/settings/product-hierarchy' } as const;
const NAME = { org: 'org chart', product: 'product tree' } as const;

export function StructureVersionFinePrint({
  table,
  id,
  column,
  mode,
  className = '',
}: {
  table: PinnedTable;
  /** The row's id — or, with `column`, the value to match. */
  id: string | null | undefined;
  column?: string;
  mode: keyof typeof LEAD;
  className?: string;
}) {
  const pins = useStructurePins(table, id, column);
  const mayOpen = useUserStore((s) => s.can('admin.settings'));
  if (!pins) return null;

  const version = (kind: 'org' | 'product', r: StructureRevision) =>
    mayOpen ? (
      <Link to={`${PAGE[kind]}#${structureHistoryAnchorId(kind)}`} className="underline decoration-dotted underline-offset-2 hover:text-[var(--text-sec)]">
        {formatRevisionVersion(r)}
      </Link>
    ) : (
      formatRevisionVersion(r)
    );

  // A tree the workspace has never versioned is left out.
  const trees = (
    [
      ['org', pins.org, pins.orgNow],
      ['product', pins.product, pins.productNow],
    ] as const
  ).filter(([, , now]) => now !== null);
  const pinned = trees.filter(([, rev]) => rev !== null);
  const unrecorded = trees.filter(([, rev]) => rev === null);
  if (trees.length === 0) return null;

  return (
    <p className={`text-[11px] leading-snug text-[var(--text-dim)] ${className}`} data-testid="structure-version-fine-print">
      {pinned.length > 0 && (
        <>
          {LEAD[mode]}{' '}
          {pinned.map(([kind, rev, now], i) => (
            <span key={kind}>
              {i > 0 && ' · '}
              {NAME[kind]} {version(kind, rev!)}
              {mode === 'started' && now!.id !== rev!.id && <> (now {version(kind, now!)})</>}
            </span>
          ))}
          .
        </>
      )}
      {unrecorded.map(([kind, , now]) => (
        <span key={kind}>
          {' '}
          {NAME[kind].charAt(0).toUpperCase() + NAME[kind].slice(1)} version not recorded — made before versions were kept (now {version(kind, now!)}).
        </span>
      ))}
    </p>
  );
}
